import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CheckCircle2, XCircle, CalendarDays, MapPin, Clock, Loader2 } from "lucide-react";

export default function PublicResponse() {
  const { t, i18n } = useTranslation("public");
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [response, setResponse] = useState(null);

  useEffect(() => {
    api.get(`/public/requests/${token}`)
      .then((r) => {
        setData(r.data);
        const st = r.data.request.status;
        if (st === "disponivel" || st === "indisponivel" || st === "confirmado") setResponse(st);
      })
      .catch((e) => setError(e.response?.data?.detail || t("response.invalid")))
      .finally(() => setLoading(false));
  }, [token, t]);

  const submit = async (available) => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/public/requests/${token}/respond`, { available });
      setResponse(res.status);
    } catch (e) {
      setError(e.response?.data?.detail || t("response.invalid"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <div className="font-display text-xl font-bold text-zinc-950">{t("response.invalid")}</div>
          <div className="text-sm text-zinc-500 mt-2">{error}</div>
        </div>
      </div>
    );
  }

  const { event, promoter, client, organization, shift } = data;
  const orgName = organization?.name || "PromoCrew";
  const orgColor = organization?.primary_color || "#0a0a0a";
  const dateLocale = i18n.language === "en" ? "en-GB" : "pt-PT";

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="public-response-page">
      <div className="text-white py-4 px-6 flex items-center justify-between gap-3" style={{ background: orgColor }}>
        <div className="flex items-center gap-3">
          {organization?.logo_url ? (
            <img src={organization.logo_url} alt="" className="h-8 w-8 rounded object-cover bg-white/10" />
          ) : null}
          <div className="font-display font-bold text-lg">{orgName}</div>
        </div>
        <LanguageSwitcher />
      </div>

      <div className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-md mx-auto bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="text-center px-6 py-6 border-b border-zinc-100 bg-zinc-50/50">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{t("response.title")}</div>
            <div className="font-display text-xl font-bold text-zinc-950 mt-1">Olá {promoter?.name?.split(" ")[0] || ""}</div>
            <div className="text-sm text-zinc-600 mt-1">{orgName}</div>
          </div>

          <div className="p-6 space-y-5">
            <div>
              <h1 className="font-display text-2xl font-bold text-zinc-950 leading-tight">{event.name}</h1>
              {client && <div className="text-sm text-zinc-500 mt-1">{client.name}</div>}
              {shift && (
                <div className="mt-2 inline-flex items-center gap-1.5 bg-zinc-950 text-white text-sm font-semibold px-3 py-1.5 rounded-full" data-testid="public-shift-badge">
                  <Clock className="w-3.5 h-3.5" /> Turno: {shift.name} · {shift.start_time}{shift.end_time ? `–${shift.end_time}` : ""}
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm text-zinc-700">
              <Row icon={CalendarDays} text={new Date(event.date).toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
              {shift ? (
                (shift.start_time) && <Row icon={Clock} text={`${shift.start_time}${shift.end_time ? ` – ${shift.end_time}` : ""}`} />
              ) : (
                event.start_time && <Row icon={Clock} text={`${event.start_time}${event.end_time ? ` – ${event.end_time}` : ""}`} />
              )}
              {event.location && <Row icon={MapPin} text={event.location} />}
            </div>

            {(event.dress_code || event.briefing || event.hourly_rate) && (
              <div className="border-t border-zinc-100 pt-4 space-y-3 text-sm">
                {event.dress_code && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-0.5">{t("response.dressCode")}</div>
                    <div className="text-zinc-700">{event.dress_code}</div>
                  </div>
                )}
                {event.briefing && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-0.5">{t("response.briefing")}</div>
                    <div className="text-zinc-700 whitespace-pre-wrap">{event.briefing}</div>
                  </div>
                )}
                {event.hourly_rate && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-0.5">{t("response.hourlyRate")}</div>
                    <div className="text-zinc-700">€{event.hourly_rate}</div>
                  </div>
                )}
              </div>
            )}

            {response ? (
              <div className={`rounded-xl p-4 text-center ${response === "indisponivel" ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"}`} data-testid="response-confirmation">
                {response === "indisponivel" ? (
                  <>
                    <XCircle className="w-8 h-8 text-red-600 mx-auto" />
                    <div className="font-semibold text-red-900 mt-2">{t("response.thanksNotAvailable")}</div>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                    <div className="font-semibold text-emerald-900 mt-2">{t("response.thanksAvailable")}</div>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2.5 pt-2">
                <button
                  data-testid="btn-available"
                  disabled={submitting}
                  onClick={() => submit(true)}
                  className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold shadow-sm transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {t("response.iAmAvailable")}
                </button>
                <button
                  data-testid="btn-unavailable"
                  disabled={submitting}
                  onClick={() => submit(false)}
                  className="w-full py-3.5 bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-800 rounded-xl font-semibold shadow-sm transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" /> {t("response.iAmNot")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-zinc-500 mt-6">
          {orgName} · PromoCrew
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-zinc-400 shrink-0" />
      <span className="capitalize-first">{text}</span>
    </div>
  );
}
